import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {ActivityLogRegularAiIcon} from 'shells/icon/svg/activity-log-regular-ai-icon';
import {AdminDigestRegularAiIcon} from 'shells/icon/svg/admin-digest-regular-ai-icon';
import {AircallRegularAiIcon} from 'shells/icon/svg/aircall-regular-ai-icon';
import {BinocularsRegularAiIcon} from 'shells/icon/svg/binoculars-regular-ai-icon';
import {CallrailRegularAiIcon} from 'shells/icon/svg/callrail-regular-ai-icon';
import {ChatbotRegularAiIcon} from 'shells/icon/svg/chatbot-regular-ai-icon';
import {CircleDollarRegularIcon} from 'shells/icon/svg/circle-dollar-regular-icon';
import {CompanySearchRegularAiIcon} from 'shells/icon/svg/company-search-regular-ai-icon';
import {CompetitorRegularAiIcon} from 'shells/icon/svg/competitor-regular-ai-icon';
import {DialpadRegularAiIcon} from 'shells/icon/svg/dialpad-regular-ai-icon';
// @ts-expect-error
import {DownloadIcon} from 'shells/icon/svg/download-icon';
import {EnvelopesRegularAiIcon} from 'shells/icon/svg/envelopes-regular-ai-icon';
import {FileTextRegularIcon} from 'shells/icon/svg/file-text-regular-icon';
import {GoogleRegularAiIcon} from 'shells/icon/svg/google-regular-ai-icon';
import {KixieRegularAiIcon} from 'shells/icon/svg/kixie-regular-ai-icon';
import {LeadSearchRegularAiIcon} from 'shells/icon/svg/lead-search-regular-ai-icon';
import {MailerRegularAiIcon} from 'shells/icon/svg/mailer-regular-ai-icon';
import {McpRegularAiIcon} from 'shells/icon/svg/mcp-regular-ai-icon';
import {MicrophoneRegularIcon} from 'shells/icon/svg/microphone-regular-icon';
import {MicrosoftTeamsRegularAiIcon} from 'shells/icon/svg/microsoft-teams-regular-ai-icon';
import {NoteRegularAiIcon} from 'shells/icon/svg/note-regular-ai-icon';
import {OverviewRegularAiIcon} from 'shells/icon/svg/overview-regular-ai-icon';
import {PersonSearchRegularAiIcon} from 'shells/icon/svg/person-search-regular-ai-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';
import {PhoneRegularAiIcon} from 'shells/icon/svg/phone-regular-ai-icon';
import {PiechartRegularAiIcon} from 'shells/icon/svg/piechart-regular-ai-icon';
import {PipelineRegularAiIcon} from 'shells/icon/svg/pipeline-regular-ai-icon';
import {ReplyArrowRegularAiIcon} from 'shells/icon/svg/reply-arrow-regular-ai-icon';
import {RingcentralRegularAiIcon} from 'shells/icon/svg/ringcentral-regular-ai-icon';
import {SalesBubbleRegularAiIcon} from 'shells/icon/svg/sales-bubble-regular-ai-icon';
import {SearchDollarRegularAiIcon} from 'shells/icon/svg/search-dollar-regular-ai-icon';
import {SearchRegularIcon} from 'shells/icon/svg/search-regular-icon';
import {TaskRegularAiIcon} from 'shells/icon/svg/task-regular-ai-icon';
import {TextRegularAiIcon} from 'shells/icon/svg/text-regular-ai-icon';
import {TimelineRegularAiIcon} from 'shells/icon/svg/timeline-regular-ai-icon';
import {UserDigestRegularAiIcon} from 'shells/icon/svg/user-digest-regular-ai-icon';
import {ZoomRegularAiIcon} from 'shells/icon/svg/zoom-regular-ai-icon';

import {AiFeatureType} from 'nutshell-graphql-types';

import type {AiFeature} from './types';

// Illustration paths - these reference SVG files in public/include/illustrations/
const AI_ILLUSTRATIONS = {
    adminDigest: 'ai-homepage/ai-admin-digest',
    aircallNotetaker: 'ai-homepage/ai-aircall-notetaker',
    c2cNotetaker: 'ai-homepage/ai-c2c-notetaker',
    c2cNotetakerCropped: 'ai-homepage/ai-c2c-notetaker-cropped',
    callrailNotetaker: 'ai-homepage/ai-callrail-notetaker',
    companyIq: 'ai-homepage/ai-company-iq',
    competitorSpy: 'ai-homepage/ai-competitor-spy',
    dialpadNotetaker: 'ai-homepage/ai-dialpad-notetaker',
    emailCampaignBuilder: 'ai-homepage/ai-email-campaign-builder',
    emailReplyStarter: 'ai-homepage/ai-email-reply-starter',
    emailSequenceWriter: 'ai-homepage/ai-email-sequence-writer',
    followUpTasks: 'ai-homepage/ai-timeline-summarizer',
    followUpTasksCropped: 'ai-homepage/ai-lead-summarizer-cropped',
    googleMeetNotetaker: 'ai-homepage/ai-google-meet-notetaker',
    kixieNotetaker: 'ai-homepage/ai-kixie-notetaker',
    leadResearcher: 'ai-homepage/ai-lead-researcher',
    leadResearcherCropped: 'ai-homepage/ai-lead-researcher-cropped',
    leadSummarizer: 'ai-homepage/ai-lead-summarizer',
    leadSummarizerCropped: 'ai-homepage/ai-lead-summarizer-cropped',
    mcpServer: 'ai-homepage/ai-mcp-server',
    microsoftTeamsNotetaker: 'ai-homepage/ai-microsoft-teams-notetaker',
    noteTranscriber: 'ai-homepage/ai-note-transcriber',
    peopleIq: 'ai-homepage/ai-people-iq',
    prospectorIq: 'ai-homepage/ai-prospector-iq',
    reportGenerator: 'ai-homepage/ai-report-generator',
    reportGeneratorCropped: 'ai-homepage/ai-report-generator-cropped',
    ringcentralNotetaker: 'ai-homepage/ai-ringcentral-notetaker',
    salesChatbot: 'ai-homepage/ai-sales-chatbot',
    salesChatbotCropped: 'ai-homepage/ai-sales-chatbot-cropped',
    salesObjectionCoach: 'ai-homepage/ai-sales-objection-coach',
    salesPipelineDesigner: 'ai-homepage/ai-sales-pipeline-designer',
    speakToLog: 'ai-homepage/ai-speak-to-log',
    speakToLogCropped: 'ai-homepage/ai-speak-to-log-cropped',
    timelineSummarizer: 'ai-homepage/ai-timeline-summarizer',
    userDigest: 'ai-homepage/ai-user-digest',
    visitorIq: 'ai-homepage/ai-visitor-iq',
    zoomNotetaker: 'ai-homepage/ai-zoom-notetaker',
} as const;

const allFeatureTypes: Array<AiFeatureType> = [
    AiFeatureType.LEAD_RESEARCHER,
    AiFeatureType.LEAD_RESEARCH,
    AiFeatureType.EMAIL_SEQUENCE_WRITER,
    AiFeatureType.SALES_OBJECTION_COACH,
    AiFeatureType.SALES_PIPELINE_DESIGNER,
    AiFeatureType.EMAIL_REPLY_STARTER,
    AiFeatureType.TIMELINE_SUMMARIZER,
    AiFeatureType.LEAD_SUMMARIZER,
    AiFeatureType.AI_FOLLOW_UP_TASKS,
    AiFeatureType.COMPANY_IQ,
    AiFeatureType.PEOPLE_IQ,
    AiFeatureType.VISITOR_IQ,
    AiFeatureType.PROSPECTOR_IQ,
    AiFeatureType.COMPETITOR_SPY,
    AiFeatureType.NOTE_TRANSCRIBER,
    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
    AiFeatureType.GOOGLE_MEET_NOTETAKER,
    AiFeatureType.ZOOM_NOTETAKER,
    AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
    AiFeatureType.RINGCENTRAL_NOTETAKER,
    AiFeatureType.AIRCALL_NOTETAKER,
    AiFeatureType.DIALPAD_NOTETAKER,
    AiFeatureType.CALLRAIL_NOTETAKER,
    AiFeatureType.KIXIE_NOTETAKER,
    AiFeatureType.SALES_CHATBOT,
    AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
    AiFeatureType.REPORT_GENERATOR,
    AiFeatureType.USER_DIGEST,
    AiFeatureType.ADMIN_DIGEST,
    AiFeatureType.SPEAK_TO_LOG,
    AiFeatureType.MCP_SERVER,
];

// Place to filter out features that are feature flagged or not valid for this
// given instance
export const AI_FEATURES: Array<AiFeature> = allFeatureTypes
    .filter((featureType) => {
        switch (featureType) {
            case AiFeatureType.LEAD_RESEARCH:
                return NutClientConfig.hasAiOutcomesFF;
            case AiFeatureType.LEAD_RESEARCHER:
                return !NutClientConfig.hasAiOutcomesFF;
            case AiFeatureType.LEAD_SUMMARIZER:
                return NutClientConfig.hasLeadSummarizationsFF;
            case AiFeatureType.AI_FOLLOW_UP_TASKS:
                return NutClientConfig.hasAiFollowUpTasksFF;
            case AiFeatureType.EMAIL_CAMPAIGN_BUILDER:
                return NutClientConfig.hasAiEmailTemplateGenerationFF;
            case AiFeatureType.REPORT_GENERATOR:
                return NutClientConfig.hasReportGeneratorFF;
            // Admin digest is only visible to admin users
            case AiFeatureType.ADMIN_DIGEST:
                return NutClientConfig.loggedInUserIsAdmin;
            default:
                return true;
        }
    })
    .map(getFeature);

export function getFeature(featureType: AiFeatureType): AiFeature {
    switch (featureType) {
        // ============================================
        // ASSISTANTS (Conversational Agents)
        // ============================================
        case AiFeatureType.LEAD_RESEARCHER:
            return {
                type: AiFeatureType.LEAD_RESEARCHER,
                pathSegment: 'lead-researcher',
                name: 'Lead researcher',
                icon: LeadSearchRegularAiIcon,
                grouping: 'assistants',
                cardDescription: 'Uncover more about your leads and get a briefing',
                cardIllustration: AI_ILLUSTRATIONS.leadResearcherCropped,
                pageDescription:
                    "Use Nutshell's conversational lead researcher to discover more about your prospects. Powered by frontier language models like ChatGPT and Claude, you'll find additional contacts, local information, and conversation starters. You can share the particular kind of research you'd like to conduct, and get a custom-tuned research report.\n\nOptionally, you can save the research report as a lead note, for quick reference in the future.",
                pageIllustration: AI_ILLUSTRATIONS.leadResearcher,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/11402118-ai-ai-agents',
                otherFeatures: [
                    AiFeatureType.EMAIL_SEQUENCE_WRITER,
                    AiFeatureType.SALES_OBJECTION_COACH,
                    AiFeatureType.SALES_PIPELINE_DESIGNER,
                ],
            };
        case AiFeatureType.LEAD_RESEARCH:
            return {
                type: AiFeatureType.LEAD_RESEARCH,
                pathSegment: 'lead-researcher',
                name: 'Lead researcher',
                icon: LeadSearchRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription: 'Get fast, focused context the moment a lead hits your CRM',
                cardIllustration: AI_ILLUSTRATIONS.leadResearcherCropped,
                pageDescription:
                    'Get fast, focused context the moment a lead hits your CRM.\n\nIt surfaces current company details, identifies additional contacts, and suggests talking points so outreach is personal from the first touch. Everything lives in your Nutshell workspace—no extra tabs or tools required.',
                pageIllustration: AI_ILLUSTRATIONS.leadResearcher,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13370681-lead-researcher',
                otherFeatures: [
                    AiFeatureType.LEAD_SUMMARIZER,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.PROSPECTOR_IQ,
                    AiFeatureType.COMPANY_IQ,
                ],
            };
        case AiFeatureType.EMAIL_SEQUENCE_WRITER:
            return {
                type: AiFeatureType.EMAIL_SEQUENCE_WRITER,
                pathSegment: 'email-sequence-writer',
                name: 'Email sequence writer',
                icon: EnvelopesRegularAiIcon,
                grouping: 'assistants',
                cardDescription: 'Get help writing multi-part emails that get noticed',
                pageDescription:
                    'Get some help writing email sequences that get replies. Tell Nutshell a bit about your business and your prospects, and get suggestions on a great series of emails. Add the sequence to your pipeline to automate your outreach.',
                pageIllustration: AI_ILLUSTRATIONS.emailSequenceWriter,
                highlights: [],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13370756-email-sequence-writer',
                otherFeatures: [
                    AiFeatureType.LEAD_RESEARCHER,
                    AiFeatureType.SALES_OBJECTION_COACH,
                    AiFeatureType.SALES_PIPELINE_DESIGNER,
                ],
            };
        case AiFeatureType.SALES_OBJECTION_COACH:
            return {
                type: AiFeatureType.SALES_OBJECTION_COACH,
                pathSegment: 'sales-objection-coach',
                name: 'Sales objection coach',
                icon: SalesBubbleRegularAiIcon,
                grouping: 'assistants',
                cardDescription: 'Your sparring partner to find ways around "not right now"',
                pageDescription:
                    'If you’re running into objections during your sales process, use our coach to brainstorm fresh ways of getting to yes. Maybe you’re hearing concerns about budget, need, or timeline — Nutshell’s conversational coach is packed full of tools to help you have successful outcomes.',
                pageIllustration: AI_ILLUSTRATIONS.salesObjectionCoach,
                highlights: [],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13370823-sales-objection-coach',
                otherFeatures: [
                    AiFeatureType.LEAD_RESEARCHER,
                    AiFeatureType.EMAIL_SEQUENCE_WRITER,
                    AiFeatureType.SALES_PIPELINE_DESIGNER,
                ],
            };
        case AiFeatureType.SALES_PIPELINE_DESIGNER:
            return {
                type: AiFeatureType.SALES_PIPELINE_DESIGNER,
                pathSegment: 'sales-pipeline-designer',
                name: 'Sales pipeline designer',
                icon: PipelineRegularAiIcon,
                grouping: 'assistants',
                cardDescription: 'Advice on the best processes and milestones for your business',
                pageDescription:
                    "Nutshell's sales process tooling supports automated tasks, custom stage goals, drip sequences, assignment rules, and more. Use our pipeline designer as a sounding board to take your sales process and turn it into best practice.\n\nOur pipeline designer knows how Nutshell works, and it's full of powerful sales strategies. Describe your business, how you sell, how you interact with leads, and the pipeline designer will help you construct a great sales process. It can also look at your existing pipeline and suggest improvements.",
                pageIllustration: AI_ILLUSTRATIONS.salesPipelineDesigner,
                highlights: [],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13370843-sales-pipeline-designer',
                otherFeatures: [
                    AiFeatureType.LEAD_RESEARCHER,
                    AiFeatureType.EMAIL_SEQUENCE_WRITER,
                    AiFeatureType.SALES_OBJECTION_COACH,
                ],
            };
        // ============================================
        // SALES ACCELERATION
        // ============================================
        case AiFeatureType.EMAIL_REPLY_STARTER:
            return {
                type: AiFeatureType.EMAIL_REPLY_STARTER,
                pathSegment: 'email-reply-starter',
                name: 'Email reply starter',
                icon: ReplyArrowRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription: 'Get suggestions on quick replies, directly from Nutshell',
                pageDescription:
                    "In sales, response time matters. Get a jump start on a reply to emails you receive from your contacts. When you're looking at a lead or contact's timeline, Nutshell will suggest several replies to help you reply as quickly as possible.",
                pageIllustration: AI_ILLUSTRATIONS.emailReplyStarter,
                highlights: [],
                video: undefined,
                learnMoreLink: undefined,
                otherFeatures: [
                    AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                ],
            };
        case AiFeatureType.TIMELINE_SUMMARIZER:
            return {
                type: AiFeatureType.TIMELINE_SUMMARIZER,
                pathSegment: 'timeline-summarizer',
                name: 'Timeline summaries',
                icon: TimelineRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    "Get a quick summary from your team's interactions with any lead or contact",
                pageDescription:
                    "Get a bird's-eye-view on the full history of a lead. Nutshell reviews every note, email and activity and summarizes it into a synopsis of how the lead is progressing.",
                pageIllustration: AI_ILLUSTRATIONS.timelineSummarizer,
                highlights: [
                    {
                        icon: PhoneRegularAiIcon,
                        title: 'Works well with calls & meetings',
                        description:
                            'Coupled with our phone and meeting integrations, Nutshell reviews your transcriptions to identify action items and questions that came up in your calls.',
                    },
                ],
                video: undefined,
                learnMoreLink: 'https://www.nutshell.com/ai/timeline-summarization',
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.ZOOM_NOTETAKER,
                    AiFeatureType.GOOGLE_MEET_NOTETAKER,
                    AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
                ],
            };
        case AiFeatureType.LEAD_SUMMARIZER:
            return {
                type: AiFeatureType.LEAD_SUMMARIZER,
                pathSegment: 'lead-summarizer',
                name: 'Lead recaps & next actions',
                icon: OverviewRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    "Learn what happened and identify what's next without opening a lead",
                cardIllustration: AI_ILLUSTRATIONS.leadSummarizerCropped,
                pageDescription:
                    "Get up to speed on a lead with a quick recap and next actions. Nutshell reviews a lead's emails, activities, tasks, as well as your pipeline stages and goals to suggest the most important thing you can do to move a lead along.\n\nRecaps and next actions are presented instantly, and without a need to generate a manual summary.",
                pageIllustration: AI_ILLUSTRATIONS.leadSummarizer,
                highlights: [
                    {
                        icon: PipelineRegularAiIcon,
                        title: 'Powered by your pipeline',
                        description:
                            'Define stage goals and stage guidance for your pipeline — Nutshell AI uses these to suggest actions.',
                    },
                    {
                        icon: PhoneRegularAiIcon,
                        title: 'Works well with calls & meetings',
                        description:
                            'Coupled with our phone and meeting integrations, Nutshell reviews your transcriptions to identify action items and questions that came up in your calls.',
                    },
                    {
                        icon: CircleDollarRegularIcon,
                        title: 'Pricing',
                        description: 'Available in Nutshell Pro and above.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://www.nutshell.com/blog/save-time-with-ai-email-reply-starters-in-nutshell',
                otherFeatures: [
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                    AiFeatureType.EMAIL_REPLY_STARTER,
                ],
            };
        case AiFeatureType.AI_FOLLOW_UP_TASKS:
            return {
                type: AiFeatureType.AI_FOLLOW_UP_TASKS,
                pathSegment: 'follow-up-tasks',
                name: 'AI follow-up tasks',
                icon: TaskRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    'Turn call and meeting summaries into ready-to-go tasks automatically',
                cardIllustration: AI_ILLUSTRATIONS.followUpTasksCropped,
                pageDescription:
                    'Your calls and meetings are already being summarized. Nutshell can automatically turn those insights into actionable tasks assigned to you—so nothing slips through the cracks.\n\nEnable the feature under AI features for your user, then complete a logged call or meeting with a summary to generate tasks.',
                pageIllustration: AI_ILLUSTRATIONS.followUpTasks,
                highlights: [
                    {
                        icon: PhoneRegularAiIcon,
                        title: 'Built on summarized activities',
                        description:
                            'After a summarized call or meeting, Nutshell proposes follow-up tasks based on what was discussed.',
                    },
                ],
                video: undefined,
                learnMoreLink: undefined,
                otherFeatures: [
                    AiFeatureType.LEAD_SUMMARIZER,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.ZOOM_NOTETAKER,
                ],
            };
        case AiFeatureType.COMPANY_IQ:
            return {
                type: AiFeatureType.COMPANY_IQ,
                pathSegment: 'company-iq',
                name: 'CompanyIQ',
                icon: CompanySearchRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription: 'Enrich companies with revenue, headcount, and more',
                pageDescription:
                    'Enrich your accounts with firmographic data like industry, employee count, revenue, and more. CompanyIQ pulls from trusted data sources to fill in the blanks, so you spend less time researching and more time building relationships.',
                pageIllustration: AI_ILLUSTRATIONS.companyIq,
                highlights: [
                    {
                        icon: SearchRegularIcon,
                        title: 'Always in control',
                        description:
                            "CompanyIQ presents new findings as it finds them, so you're in the driver's seat on the information in Nutshell.",
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://www.nutshell.com/blog/nutshell-announces-company-enrichment',
                otherFeatures: [
                    AiFeatureType.PEOPLE_IQ,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                    AiFeatureType.COMPETITOR_SPY,
                ],
            };
        case AiFeatureType.PEOPLE_IQ:
            return {
                type: AiFeatureType.PEOPLE_IQ,
                pathSegment: 'people-iq',
                name: 'PeopleIQ',
                icon: PersonSearchRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    'Find additional points of contact at the businesses you already know',
                pageDescription:
                    "Identify additional contacts for every lead. Nutshell's contact enrichment finds new points of contact for your prospects. From a company or lead use the PeopleIQ lightbulb to find people who may work there. Add them to Nutshell with a click, including their role and LinkedIn profile.",
                pageIllustration: AI_ILLUSTRATIONS.peopleIq,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/10487090-peopleiq',
                otherFeatures: [
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                    AiFeatureType.COMPETITOR_SPY,
                ],
            };
        case AiFeatureType.VISITOR_IQ:
            return {
                type: AiFeatureType.VISITOR_IQ,
                pathSegment: 'visitor-iq',
                name: 'VisitorIQ',
                icon: BinocularsRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    'Identify the businesses that are browsing your site, even if they never fill a form',
                pageDescription:
                    'Turn anonymous website traffic into actionable leads. VisitorIQ identifies the companies visiting your site and surfaces them in Nutshell, so your sales team can reach out while interest is high.\n\nFilter visitors by page views, time on site, or specific pages visited to prioritize the hottest prospects.',
                pageIllustration: AI_ILLUSTRATIONS.visitorIq,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13376671-visitoriq',
                otherFeatures: [
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.PEOPLE_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                    AiFeatureType.COMPETITOR_SPY,
                ],
            };
        case AiFeatureType.PROSPECTOR_IQ:
            return {
                type: AiFeatureType.PROSPECTOR_IQ,
                pathSegment: 'prospector-iq',
                name: 'ProspectorIQ',
                icon: SearchDollarRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription: 'Target new leads based on your ideal customer profile',
                pageDescription:
                    'Build targeted prospect lists without leaving Nutshell. ProspectorIQ lets you search for companies and contacts by industry, location, company size, and more — then add them directly to your CRM with a single click.\n\nEach prospect comes with emails and phone numbers, so you can start connecting immediately.',
                pageIllustration: AI_ILLUSTRATIONS.prospectorIq,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13376618-prospectoriq',
                otherFeatures: [
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.PEOPLE_IQ,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.COMPETITOR_SPY,
                ],
            };
        case AiFeatureType.COMPETITOR_SPY:
            return {
                type: AiFeatureType.COMPETITOR_SPY,
                pathSegment: 'competitor-spy',
                name: 'CompetitorSpy',
                icon: CompetitorRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    'See how your website and visitor metrics stack up to your competition',
                pageDescription:
                    "CompetitorSpy provides insights into your competitors' website traffic, marketing tactics, and more, so you can level up your strategies. Plug in the domains of five competitors and compare your website's performance.",
                pageIllustration: AI_ILLUSTRATIONS.competitorSpy,
                highlights: [],
                hasNoStats: true,
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13376637-competitorspy',
                otherFeatures: [
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.PEOPLE_IQ,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                ],
            };
        case AiFeatureType.NOTE_TRANSCRIBER:
            return {
                type: AiFeatureType.NOTE_TRANSCRIBER,
                pathSegment: 'note-transcriber',
                name: 'Note transcriber',
                icon: NoteRegularAiIcon,
                grouping: 'salesAcceleration',
                cardDescription:
                    'Speak to Nutshell and get instant transcripts for notes and activity logs',
                pageDescription:
                    'Turn voice memos into searchable text notes. Record your notes with your microphone after a meeting or site visit, and Nutshell will transcribe it and turn it into a note.',
                pageIllustration: AI_ILLUSTRATIONS.noteTranscriber,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/10485878-note-transcriber',
                otherFeatures: [
                    AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.EMAIL_REPLY_STARTER,
                ],
            };
        // ============================================
        // NOTETAKERS (Meeting & Phone)
        // ============================================
        case AiFeatureType.GOOGLE_MEET_NOTETAKER:
            return {
                type: AiFeatureType.GOOGLE_MEET_NOTETAKER,
                pathSegment: 'google-meet-notetaker',
                name: 'Google Meet notetaker',
                icon: GoogleRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Automatically log and summarize your meetings to Nutshell',
                pageDescription:
                    'Save hours each week by automatically logging and summarizing your meetings in Google Meet. When you complete a Gmeet with a customer or a lead, Nutshell will automatically log it as an activity, import the recording, transcribe it, and show a concise summary of the call.\n\nThe worst part of using a CRM is manually logging activities, and with our Google Meet integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.googleMeetNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your meetings to get concise summaries and takeaways for each meeting.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log all meetings, or to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatically create contacts',
                        description:
                            'Nutshell will automatically create contacts for new participants in your Google Meets.',
                    },
                    {
                        icon: GoogleRegularAiIcon,
                        title: 'Plays well with friends',
                        description:
                            'You can also use Google Meet with Nutshell Scheduler to collect bookings with Google Meet links attached.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/11792031-google-meet-notetaker',
                otherFeatures: [
                    AiFeatureType.ZOOM_NOTETAKER,
                    AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                ],
            };
        case AiFeatureType.ZOOM_NOTETAKER:
            return {
                type: AiFeatureType.ZOOM_NOTETAKER,
                pathSegment: 'zoom-notetaker',
                name: 'Zoom notetaker',
                icon: ZoomRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Automatically log and summarize your meetings to Nutshell',
                pageDescription:
                    'Save hours each week by automatically logging and summarizing your Zoom meetings. When you complete a Zoom call with a customer or a lead, Nutshell will automatically log it as an activity, import the recording, transcribe it, and show a concise summary of the meeting.\n\nThe worst part of using a CRM is manually logging activities, and with our Zoom integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.zoomNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your meetings to get concise summaries and takeaways for each meeting.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log all meetings, or to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatically create contacts',
                        description:
                            'Nutshell will automatically create contacts for new participants in your Zoom meetings.',
                    },
                    {
                        icon: ZoomRegularAiIcon,
                        title: 'Plays well with friends',
                        description:
                            'You can also use Zoom with Nutshell Scheduler to collect bookings with Zoom links attached.',
                    },
                ],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/10852788-zoom-notetaker',
                otherFeatures: [
                    AiFeatureType.GOOGLE_MEET_NOTETAKER,
                    AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                ],
            };
        case AiFeatureType.MICROSOFT_TEAMS_NOTETAKER:
            return {
                type: AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
                pathSegment: 'microsoft-teams-notetaker',
                name: 'Microsoft Teams notetaker',
                icon: MicrosoftTeamsRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Automatically log and summarize your meetings to Nutshell',
                pageDescription:
                    'Save hours each week by automatically logging and summarizing your meetings in Microsoft Teams. When you complete a Teams meeting with a customer or a lead, Nutshell will automatically log it as an activity, import the recording, transcribe it, and show a concise summary of the call.\n\nThe worst part of using a CRM is manually logging activities, and with our Microsoft Teams integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.microsoftTeamsNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your meetings to get concise summaries and takeaways for each meeting.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log all meetings, or to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatically create contacts',
                        description:
                            'Nutshell will automatically create contacts for new participants in your Teams meetings.',
                    },
                    {
                        icon: MicrosoftTeamsRegularAiIcon,
                        title: 'Plays well with friends',
                        description:
                            'You can also use Microsoft Teams with Nutshell Scheduler to collect bookings with Teams links attached.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13376699-microsoft-teams-notetaker',
                otherFeatures: [
                    AiFeatureType.GOOGLE_MEET_NOTETAKER,
                    AiFeatureType.ZOOM_NOTETAKER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                ],
            };
        case AiFeatureType.CLICK_TO_CALL_NOTETAKER:
            return {
                type: AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                pathSegment: 'click-to-call-notetaker',
                name: 'Click-to-call notetaker',
                icon: PhoneRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Dial, log, and summarize calls, directly from your browser',
                cardIllustration: AI_ILLUSTRATIONS.c2cNotetakerCropped,
                pageDescription:
                    'Make phone calls without leaving Nutshell. You can dial, talk, record, log, and summarize your calls — directly from your web browser. Click-to-call uses your own caller ID, so return calls come back directly to you.\n\nThe worst part of using a CRM is manually logging activities, and with our Click-to-call integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.c2cNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description: 'Get concise summaries and takeaways from every call.',
                    },
                    {
                        icon: MicrophoneRegularIcon,
                        title: 'Crystal-clear audio',
                        description:
                            'Works with your bluetooth headset using our industry-leading telephony provider.',
                    },
                    {
                        icon: CircleDollarRegularIcon,
                        title: 'Pricing',
                        description: 'Available in Nutshell Pro and above.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/10943538-click-to-call-notetaker',
                otherFeatures: [
                    AiFeatureType.RINGCENTRAL_NOTETAKER,
                    AiFeatureType.AIRCALL_NOTETAKER,
                    AiFeatureType.DIALPAD_NOTETAKER,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                    AiFeatureType.KIXIE_NOTETAKER,
                ],
            };
        case AiFeatureType.RINGCENTRAL_NOTETAKER:
            return {
                type: AiFeatureType.RINGCENTRAL_NOTETAKER,
                pathSegment: 'ringcentral-notetaker',
                name: 'RingCentral notetaker',
                icon: RingcentralRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Sync, log, and summarize every call',
                pageDescription:
                    'Save hours each week by automatically syncing, logging and summarizing every call you make with RingCentral. When you complete a phone call with a customer or a lead, Nutshell will automatically log it as an activity, import and transcribe the recording, and show a concise summary.\n\nThe worst part of using a CRM is manually logging activities, and with our RingCentral integration, you can put an end to that. Our own sales team at Nutshell uses RingCentral for no-click activity logging.',
                pageIllustration: AI_ILLUSTRATIONS.ringcentralNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your calls to get concise summaries and takeaways for each conversation.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log and share all calls, or just to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatic contact mapping',
                        description:
                            'Nutshell will automatically attach calls to your contacts using caller ID.',
                    },
                    {
                        icon: PhoneOutlineIcon,
                        title: 'Compatible with all RingCentral apps',
                        description:
                            'Whether you use a physical phone, mobile app, or a desktop softphone, your calls sync to Nutshell.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13376863-ringcentral-notetaker',
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.AIRCALL_NOTETAKER,
                    AiFeatureType.DIALPAD_NOTETAKER,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                    AiFeatureType.KIXIE_NOTETAKER,
                ],
            };
        case AiFeatureType.AIRCALL_NOTETAKER:
            return {
                type: AiFeatureType.AIRCALL_NOTETAKER,
                pathSegment: 'aircall-notetaker',
                name: 'Aircall notetaker',
                icon: AircallRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Sync, log, and summarize every call',
                pageDescription:
                    'Save hours each week by automatically syncing, logging and summarizing every call you make with Aircall. When you complete a phone call with a customer or a lead, Nutshell will automatically log it as an activity, import and transcribe the recording, and show a concise summary.\n\nThe worst part of using a CRM is manually logging activities, and with our Aircall integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.aircallNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your calls to get concise summaries and takeaways for each conversation.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log and share all calls, or just to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatic contact mapping',
                        description:
                            'Nutshell will automatically attach calls to your contacts using caller ID.',
                    },
                    {
                        icon: PhoneOutlineIcon,
                        title: 'Compatible with all Aircall calls',
                        description: 'Works directly with Aircall APIs to sync every call.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13376874-aircall-notetaker',
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.RINGCENTRAL_NOTETAKER,
                    AiFeatureType.DIALPAD_NOTETAKER,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                    AiFeatureType.KIXIE_NOTETAKER,
                ],
            };
        case AiFeatureType.DIALPAD_NOTETAKER:
            return {
                type: AiFeatureType.DIALPAD_NOTETAKER,
                pathSegment: 'dialpad-notetaker',
                name: 'Dialpad notetaker',
                icon: DialpadRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Sync, log, and summarize every call',
                pageDescription:
                    'Save hours each week by automatically syncing, logging and summarizing every call you make with Dialpad. When you complete a phone call with a customer or a lead, Nutshell will automatically log it as an activity, import and transcribe the recording, and show a concise summary.\n\nThe worst part of using a CRM is manually logging activities, and with our Dialpad integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.dialpadNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your calls to get concise summaries and takeaways for each conversation.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log and share all calls, or just to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatic contact mapping',
                        description:
                            'Nutshell will automatically attach calls to your contacts using caller ID.',
                    },
                    {
                        icon: PhoneOutlineIcon,
                        title: 'Compatible with all Dialpad calls',
                        description: 'Works directly with Dialpad APIs to sync every call.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13376892-dialpad-notetaker',
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.RINGCENTRAL_NOTETAKER,
                    AiFeatureType.AIRCALL_NOTETAKER,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                    AiFeatureType.KIXIE_NOTETAKER,
                ],
            };
        case AiFeatureType.CALLRAIL_NOTETAKER:
            return {
                type: AiFeatureType.CALLRAIL_NOTETAKER,
                pathSegment: 'callrail-notetaker',
                name: 'CallRail notetaker',
                icon: CallrailRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Sync, log, and summarize every call',
                pageDescription:
                    'Save hours each week by automatically syncing, logging and summarizing every call you make with CallRail. When you complete a phone call with a customer or a lead, Nutshell will automatically log it as an activity, import and transcribe the recording, and show a concise summary.\n\nThe worst part of using a CRM is manually logging activities, and with our CallRail integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.callrailNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your calls to get concise summaries and takeaways for each conversation.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log and share all calls, or just to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatic contact mapping',
                        description:
                            'Nutshell will automatically attach calls to your contacts using caller ID.',
                    },
                    {
                        icon: PhoneOutlineIcon,
                        title: 'Compatible with all CallRail calls',
                        description: 'Works directly with CallRail APIs to sync every call.',
                    },
                ],
                video: undefined,
                learnMoreLink:
                    'https://support.nutshell.com/en/articles/13376910-callrail-notetaker',
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.RINGCENTRAL_NOTETAKER,
                    AiFeatureType.AIRCALL_NOTETAKER,
                    AiFeatureType.DIALPAD_NOTETAKER,
                    AiFeatureType.KIXIE_NOTETAKER,
                ],
            };
        case AiFeatureType.KIXIE_NOTETAKER:
            return {
                type: AiFeatureType.KIXIE_NOTETAKER,
                pathSegment: 'kixie-notetaker',
                name: 'Kixie notetaker',
                icon: KixieRegularAiIcon,
                grouping: 'notetakers',
                cardDescription: 'Sync, log, and summarize every call',
                pageDescription:
                    'Save hours each week by automatically syncing, logging and summarizing every call you make with Kixie. When you complete a phone call with a customer or a lead, Nutshell will automatically log it as an activity, import and transcribe the recording, and show a concise summary.\n\nThe worst part of using a CRM is manually logging activities, and with our Kixie integration, you can put an end to that.',
                pageIllustration: AI_ILLUSTRATIONS.kixieNotetaker,
                highlights: [
                    {
                        icon: NoteRegularAiIcon,
                        title: 'AI summaries',
                        description:
                            'Record your calls to get concise summaries and takeaways for each conversation.',
                    },
                    {
                        icon: FileTextRegularIcon,
                        title: 'Controls for automatic logging',
                        description:
                            'Choose to automatically log and share all calls, or just to sync them for individual review.',
                    },
                    {
                        icon: PersonSearchRegularAiIcon,
                        title: 'Automatic contact mapping',
                        description:
                            'Nutshell will automatically attach calls to your contacts using caller ID.',
                    },
                    {
                        icon: PhoneOutlineIcon,
                        title: 'Compatible with all Kixie calls',
                        description: 'Works directly with Kixie APIs to sync every call.',
                    },
                ],
                video: undefined,
                learnMoreLink: undefined,
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.RINGCENTRAL_NOTETAKER,
                    AiFeatureType.AIRCALL_NOTETAKER,
                    AiFeatureType.DIALPAD_NOTETAKER,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                ],
            };
        // ============================================
        // MARKETING & LEAD GENERATION
        // ============================================
        case AiFeatureType.EMAIL_CAMPAIGN_BUILDER:
            return {
                type: AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
                pathSegment: 'email-campaign-builder',
                name: 'One-click email campaign builder',
                icon: MailerRegularAiIcon,
                grouping: 'marketing',
                cardDescription: 'Generate personalized email campaigns in seconds',
                pageDescription:
                    'Launch a professional email campaign in seconds. Describe your audience and your goal, provide a website with content and branding, and Nutshell will generate a ready-to-send campaign — complete with subject lines, copy, branding and images.\n\nEdit the generated emails to match your voice, or use the Nutshell drag-and-drop editor to tune the visuals for your campaign.',
                pageIllustration: AI_ILLUSTRATIONS.emailCampaignBuilder,
                highlights: [],
                video: undefined,
                learnMoreLink: undefined,
                otherFeatures: [
                    AiFeatureType.EMAIL_REPLY_STARTER,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                ],
            };
        case AiFeatureType.SALES_CHATBOT:
            return {
                type: AiFeatureType.SALES_CHATBOT,
                pathSegment: 'sales-chatbot',
                name: 'Sales chatbot',
                icon: ChatbotRegularAiIcon,
                grouping: 'marketing',
                cardDescription:
                    'Answer questions and generate leads, with a chatbot trained on your own content',
                cardIllustration: AI_ILLUSTRATIONS.salesChatbotCropped,
                pageDescription:
                    "Add the Nutshell sales chatbot on your website to answer questions from your visitors and convert them to leads. Pre-trained based on your website's content, it can provide answers, collect bookings, forms, and phone calls.\n\nTry a preview of the pre-trained chatbot in 30 seconds, and add it to your site with a single snippet.",
                pageIllustration: AI_ILLUSTRATIONS.salesChatbot,
                highlights: [
                    {
                        icon: SearchDollarRegularAiIcon,
                        title: 'Focused on lead capture',
                        description:
                            'The chatbot is designed to answer questions, while steering your visits into Nutshell leads. Collect the custom fields that matter most, and automatically turn chats into leads for your sales team.',
                    },
                    {
                        icon: TextRegularAiIcon,
                        title: 'Powered by the Nutshell tracking snippet',
                        description:
                            'Uses the same snippet that powers Nutshell Analytics. Add it to your site with a simple copy/paste, or with our Wordpress extension.',
                    },
                ],
                video: undefined,
                learnMoreLink: 'https://www.nutshell.com/ai/ai-chatbot',
                otherFeatures: [
                    AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.PEOPLE_IQ,
                ],
            };
        // ============================================
        // REPORTING AND REMINDERS
        // ============================================
        case AiFeatureType.REPORT_GENERATOR:
            return {
                type: AiFeatureType.REPORT_GENERATOR,
                pathSegment: 'report-generator',
                name: 'Smart reports',
                icon: PiechartRegularAiIcon,
                grouping: 'reporting',
                cardDescription:
                    'Tap into Nutshell’s powerful reports by asking what you’re looking for in plain English',
                cardIllustration: AI_ILLUSTRATIONS.reportGeneratorCropped,
                pageDescription:
                    'Create polished sales reports in minutes, not hours. Describe the metrics and timeframe you need, and Nutshell will pull the data and generate charts — ready to share with your team or your leadership.\n\nPowered by Nutshell’s 12 foundation reports with charts, summaries and table data.',
                pageIllustration: AI_ILLUSTRATIONS.reportGenerator,
                highlights: [
                    {
                        icon: DownloadIcon,
                        title: 'Export-ready',
                        description:
                            'Download your report as an image or CSV file — ready for a slide deck or for your Excel project.',
                    },
                ],
                hasNoStats: false,
                video: undefined,
                learnMoreLink: undefined, // TODO: add once we get a KBA for this feature
                otherFeatures: [
                    AiFeatureType.EMAIL_CAMPAIGN_BUILDER,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                    AiFeatureType.CALLRAIL_NOTETAKER,
                ],
            };
        case AiFeatureType.USER_DIGEST:
            return {
                type: AiFeatureType.USER_DIGEST,
                pathSegment: 'user-digest',
                name: 'User digest',
                icon: UserDigestRegularAiIcon,
                grouping: 'reporting',
                cardDescription:
                    'A morning email to review your opportunities, hot leads, tasks, and areas of focus',
                pageDescription:
                    'Start your day with a personalized email briefing of your leads. Get reminders of your upcoming tasks and activities, and a peak into your personal pipeline.\n\nNutshell includes AI recaps & next steps for your key leads: closing soon, slipping through the cracks, hot leads and more.',
                pageIllustration: AI_ILLUSTRATIONS.userDigest,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13369982-email-digests',
                otherFeatures: [
                    AiFeatureType.REPORT_GENERATOR,
                    AiFeatureType.ADMIN_DIGEST,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                ],
            };
        case AiFeatureType.ADMIN_DIGEST:
            return {
                type: AiFeatureType.ADMIN_DIGEST,
                pathSegment: 'admin-digest',
                name: 'Admin digest',
                icon: AdminDigestRegularAiIcon,
                grouping: 'reporting',
                cardDescription:
                    'Start your week with a plan: next actions for your best leads, cold opportunities, a pipeline review and more.',
                pageDescription:
                    "Get a clear picture of your team's sales activity without digging through dashboards. The admin digest delivers a daily or weekly summary of your team's performance straight to your inbox, highlighting top sales won, hottest new leads, and key pipeline metrics so you always know where things stand.\n\nGet AI-powered insights that drive action. Each digest surfaces intelligent lead recommendations—like flagging a prospect who's trialing a competitor—along with suggested next steps, helping your team prioritize follow-ups that are most likely to close.\n\nTrack rep performance and activity trends at a glance. See who's winning deals, monitor pipeline health with close rates and stage breakdowns, and keep tabs on logged activities so you can coach your team and spot issues before they become problems.",
                pageIllustration: AI_ILLUSTRATIONS.adminDigest,
                highlights: [],
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/13369982-email-digests',
                otherFeatures: [
                    AiFeatureType.REPORT_GENERATOR,
                    AiFeatureType.USER_DIGEST,
                    AiFeatureType.TIMELINE_SUMMARIZER,
                ],
            };
        // ============================================
        // OTHER
        // ============================================
        case AiFeatureType.MCP_SERVER:
            return {
                type: AiFeatureType.MCP_SERVER,
                pathSegment: 'mcp-server',
                name: 'MCP server (ChatGPT integration)',
                icon: McpRegularAiIcon,
                grouping: 'other',
                cardDescription:
                    'Connect your Nutshell data directly to ChatGPT. Use ChatGPT like you always do, and allow it to tap into your CRM',
                pageDescription:
                    'Connect Nutshell to ChatGPT and other AI assistants using our MCP server. Once connected, you can ask questions about your pipeline, look up contacts, and get sales insights — all from within your favorite AI tool.',
                pageIllustration: AI_ILLUSTRATIONS.mcpServer,
                highlights: [
                    {
                        icon: MicrophoneRegularIcon,
                        title: 'Your CRM, conversationally',
                        description:
                            'Ask things like "What deals are closing this month?" or "Show me my hottest leads" and get answers pulled directly from Nutshell.',
                    },
                ],
                hasNoStats: true,
                video: undefined,
                learnMoreLink: 'https://support.nutshell.com/en/articles/12631143-mcp-server',
                otherFeatures: [
                    AiFeatureType.PEOPLE_IQ,
                    AiFeatureType.COMPANY_IQ,
                    AiFeatureType.VISITOR_IQ,
                    AiFeatureType.PROSPECTOR_IQ,
                ],
            };
        case AiFeatureType.SPEAK_TO_LOG:
            return {
                type: AiFeatureType.SPEAK_TO_LOG,
                pathSegment: 'speak-to-log',
                name: 'Speak to log (For mobile)',
                icon: ActivityLogRegularAiIcon,
                grouping: 'other',
                cardDescription:
                    'Use Nutshell’s voice capture mode to log an activity by simply recording your voice',
                cardIllustration: AI_ILLUSTRATIONS.speakToLogCropped,
                pageDescription:
                    'Talk out your activity and Nutshell AI will populate fields, create pending people/companies, and fully prepare your activity all for you to just click log.',
                pageIllustration: AI_ILLUSTRATIONS.speakToLog,
                highlights: [
                    {
                        icon: MicrophoneRegularIcon,
                        title: 'Voice capture mode',
                        description:
                            'Talk to text your activity while still being able to edit and revise before sending off to Nutshell AI.',
                    },
                ],
                video: undefined,
                learnMoreLink: undefined,
                otherFeatures: [
                    AiFeatureType.CLICK_TO_CALL_NOTETAKER,
                    AiFeatureType.NOTE_TRANSCRIBER,
                    AiFeatureType.ZOOM_NOTETAKER,
                    AiFeatureType.GOOGLE_MEET_NOTETAKER,
                    AiFeatureType.MICROSOFT_TEAMS_NOTETAKER,
                ],
            };
        default:
            exhaustivenessCheck(featureType);
    }
}

export const AI_FEATURES_CONFIG_MAP: Map<AiFeatureType, AiFeature> = new Map(
    AI_FEATURES.map((f) => [f.type, f])
);

const EXPLORE_PAGE_FEATURE_TYPES: AiFeatureType[] = [
    NutClientConfig.hasAiOutcomesFF ? AiFeatureType.LEAD_RESEARCH : AiFeatureType.LEAD_RESEARCHER,
    AiFeatureType.SALES_PIPELINE_DESIGNER,
    AiFeatureType.SALES_OBJECTION_COACH,
    AiFeatureType.EMAIL_REPLY_STARTER,
    AiFeatureType.NOTE_TRANSCRIBER,
];

export const EXPLORE_PAGE_FEATURES: AiFeature[] = EXPLORE_PAGE_FEATURE_TYPES.map(getFeature);
